<template lang="pug">
.stepper-footer
  .stepper-buttons
    .cancel-button-container
      #cancel-system-check-button.button-with-confirm
        Button(
          v-if="!unsafeContinue",
          :text="cancelButtonText",
          :ghost="!cancelling",
          :secondary="cancelling",
          small,
          dataName="cancel-button",
          @click="cancelClick"
        )
    Button.disable-slow-transition(
      v-show="isNextAvailable && !cancelling",
      :key="nextClass",
      :text="nextText",
      :class="[nextClass]",
      :loading="checking",
      :disabled="nextBtnDisabled",
      small,
      dataName="next-step-button",
      @click="nextStep"
    )
    #complete-system-check-button.button-with-confirm
      Button(
        v-if="showCompleteButton && !cancelling",
        :key="completeButtonClass",
        :text="completeButtonText",
        :class="completeButtonClass",
        dataName="complete-button",
        small,
        @click="completeAction"
      )
    Button(
      v-if="lastAndFailing && !cancelling && !unsafeContinue",
      text="Retake",
      primary,
      small,
      dataName="retake-button",
      @click="retake"
    )
  Confirminator(
    v-if="cancelling",
    position="above",
    contentWidth="200px",
    elementId="cancel-system-check-button",
    buttonText="End Now",
    :bodyText="cancelPopupText",
    @cancelled="cancelling = false",
    @confirmed="toVapor"
  )
  Confirminator(
    v-if="unsafeContinue",
    position="above",
    contentWidth="150px",
    elementId="complete-system-check-button",
    buttonText="I Understand",
    bodyText="If you move ahead without passing the system checkpoints your platform experience could fail.",
    @cancelled="unsafeContinue = false",
    @confirmed="completeRegistration"
  )
</template>
<script lang="ts">
import { Vue, Component } from "vue-property-decorator";
import { BaseButton as Button, EventContentModel } from "@cruciallearning/puddle";
import { Confirminator, Icon } from "@cruciallearning/puddle/components";
import { mapGetters, mapMutations, mapState } from "vuex";
import config from "@/config";
import { SystemCheckStep } from "@/types/SystemCheck";
import NavUtil from "@/utils/NavUtil";
@Component({
  components: {
    Button,
    Confirminator,
    Icon,
  },
  methods: {
    ...mapMutations("SystemCheckModule", ["nextStep", "retake"]),
  },
  computed: {
    ...mapState("SystemCheckModule", ["checking"]),
    ...mapState("EventModule", ["event"]),
    ...mapGetters("SystemCheckModule", [
      "isNextAvailable",
      "isFirstStep",
      "isLastStep",
      "isActiveSucceeded",
      "isActiveAuto",
      "isActiveDone",
      "getFailedSteps",
    ]),
  },
})
export default class StepFooter extends Vue {
  private readonly isFirstStep!: boolean;
  private readonly isLastStep!: boolean;
  private readonly isActiveSucceeded!: boolean;
  private readonly checking!: boolean;
  private readonly isActiveAuto!: boolean;
  private readonly isActiveDone!: boolean;
  private readonly getFailedSteps!: SystemCheckStep[];
  private readonly event!: EventContentModel;

  private cancelling = false;
  private unsafeContinue = false;

  get lastAndFailing(): boolean {
    return this.isLastStep && this.hasFailures;
  }

  get isFailingRequired(): boolean {
    return this.getFailedSteps.filter((e) => e.required).length > 0;
  }

  get hasFailures(): boolean {
    return this.getFailedSteps.length != 0;
  }

  get completeButtonText(): string {
    if (!this.isRegistration) return "Done";
    if (this.hasFailures) {
      if (this.unsafeContinue) {
        return "Return";
      } else {
        return "Proceed Anyway";
      }
    }
    return "Complete Registration";
  }

  get isRegistration(): boolean {
    return this.event.pendingCode != null;
  }

  get showCompleteButton(): boolean {
    if (!this.isLastStep) return false;
    if (!this.isRegistration) return true;
    return !this.isFailingRequired;
  }

  get completeButtonClass(): string {
    let classes = this.hasFailures ? "secondary" : "primary";
    classes += " wide";
    return classes;
  }

  get cancelPopupText(): string {
    return this.isRegistration
      ? "Your course registration will not be finalized until you complete the system check."
      : "Are you sure?";
  }

  get nextText(): string {
    if (this.isFirstStep) return "Begin";
    if (this.seeAboveCheck) return "See Above";
    if (this.checking) return "Checking ...";
    return "Next";
  }

  get seeAboveCheck(): boolean {
    return !this.isActiveAuto && !this.isActiveDone;
  }

  get nextClass(): string {
    if (this.isFirstStep) return "primary";
    if (this.isActiveSucceeded) return "confirm-loader";
    return "secondary";
  }

  get nextBtnDisabled(): boolean {
    return (this.seeAboveCheck || this.checking) && !this.isFirstStep;
  }
  completeAction(): void {
    if (!this.isRegistration) this.toVapor();
    else {
      if (this.hasFailures) {
        this.unsafeContinue = !this.unsafeContinue;
      } else {
        this.completeRegistration();
      }
    }
  }

  completeRegistration(): void {
    const errors: { error: string; step: string }[] = this.getFailedSteps.map((e) => ({
      error: e.error,
      step: e.name,
    }));
    this.$api.registration.completeRegistration(config.backendUrl, this.event.id, errors).then((event) => {
      if (event) {
        let eventUrl = "";
        if (event.bundleInstanceId) {
          eventUrl = `bundle/${event.id}`;
        } else {
          eventUrl = `event/${event.id}/1`;
        }
        window.open(`${config.vaporUrl}/${eventUrl}`, "_self");
      } else {
        console.error("Registration was not able to be completed!");
      }
    });
  }

  get cancelButtonText(): string {
    return this.cancelling ? "Return" : "Cancel System Check";
  }

  toVapor(): void {
    if (this.cancelling) {
      const url = `${config.vaporUrl}/events`;
      window.open(url, "_self");
    } else {
      // Event specific
      NavUtil.toVapor();
    }
  }

  confirmCancelled(): void {
    this.cancelling = false;
  }
  cancelClick(): void {
    this.cancelling = !this.cancelling;
  }
}
</script>
<style lang="scss" scoped>
.stepper-footer {
  bottom: 0;
  width: 100%;
  background-color: white;
  height: 68px;
  border: 2px solid var(--gray-20);
}
.stepper-buttons {
  height: 100%;
  display: flex;
  align-items: center;
}
.stepper-buttons .button {
  margin-right: 1rem;
  min-width: 150px;
}
.error-buttons {
  display: contents;
}
.confirmation-title {
  text-transform: capitalize;
  margin-left: 0.5rem;
}
.cancel-button-container {
  width: 376px;
  margin-left: 24px;
}
// outer button class required to maintain id through
// reactive button changes
.button-with-confirm {
  height: fit-content;
  width: fit-content;
}
.cancel-button-container .button {
  margin-right: 0;
  min-width: 200px !important;
  max-width: 200px !important;
  // must be above confirm dialog
  z-index: 210;
  position: relative;
}
#complete-system-check-button {
  margin-right: 1rem;
  z-index: 210;
}
.visibility-hidden {
  &.button {
    transition-duration: 0ms;
  }
  visibility: hidden;
}
.confirm-loader {
  background: linear-gradient(to right, var(--info-80) 50%, var(--info-20) 0);
  background-size: 200% 100%;
  background-position: right;
  animation: colorFade 1s forwards;
  border: 1px solid var(--info-80);
  pointer-events: none;
}
.disable-slow-transition {
  transition-duration: 0ms;
}
@keyframes colorFade {
  100% {
    background-position: left;
  }
}

@-webkit-keyframes colorFade {
  100% {
    background-position: left;
  }
}
</style>
